import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Save, FileText, Plus, Trash2, Goal, Square, Flag, UserPlus, ClipboardCheck, PenLine, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SignaturePad } from "@/components/shared/SignaturePad";
import { foulAlertMessage } from "@/lib/foul-alert";
import {
  getPublicScoresheet, updatePublicScoresheet,
} from "@/lib/api/public-scoresheet.functions";
import { lookupCpfPublic } from "@/lib/api/public-opponent.functions";
import { openScoresheetPdf, type ScoresheetPdfData } from "@/lib/pdf/scoresheet-pdf";



export const Route = createFileRoute("/sumula/$id")({
  validateSearch: (s: Record<string, unknown>) => ({
    t: typeof s.t === "string" ? s.t : "",
  }),
  head: () => ({
    meta: [
      { title: "Editar Súmula do Jogo" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PublicScoresheetEdit,
});


type Player = {
  student_id?: string | null;
  name: string;
  jersey_number?: string | null;
  goals: number;
  yellow_cards: number;
  red_cards: number;
  fouls: number;
  category?: string | null;
  is_above_category?: boolean;
  photo_url?: string | null;
  present?: boolean;
  cpf?: string | null;
  birth_date?: string | null;
};

function calcAge(iso?: string | null): number | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const b = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const t = new Date();
  let a = t.getFullYear() - b.getFullYear();
  const md = t.getMonth() - b.getMonth();
  if (md < 0 || (md === 0 && t.getDate() < b.getDate())) a--;
  return a >= 0 && a < 120 ? a : null;
}
function maskCpf(v: string) { const d = v.replace(/\D/g,"").slice(0,11); return d.replace(/(\d{3})(\d{3})?(\d{3})?(\d{2})?/, (_,a,b,c,e)=>[a,b,c,e].filter(Boolean).join(".").replace(/\.(\d{2})$/,"-$1")); }


type ActionKind = "goal" | "card" | "foul";

function fmtDate(d?: string | null) {
  if (!d) return "-";
  try { return new Date(d + "T00:00:00").toLocaleDateString("pt-BR"); } catch { return d; }
}

function PublicScoresheetEdit() {
  const { id } = Route.useParams();
  const { t: token } = Route.useSearch();
  const getFn = useServerFn(getPublicScoresheet);
  const saveFn = useServerFn(updatePublicScoresheet);


  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<any>(null);
  const [refereeName, setRefereeName] = useState("");
  const [refereeReport, setRefereeReport] = useState("");
  const [visitorName, setVisitorName] = useState("");
  const [host, setHost] = useState<Player[]>([]);
  const [visitor, setVisitor] = useState<Player[]>([]);
  const [sigReferee, setSigReferee] = useState<string | null>(null);
  const [sigHost, setSigHost] = useState<string | null>(null);
  const [sigVisitor, setSigVisitor] = useState<string | null>(null);
  const [sigEpoch, setSigEpoch] = useState(0);

  const [actionOpen, setActionOpen] = useState(false);
  const [refereeAlert, setRefereeAlert] = useState<string>("");
  const [actionKind, setActionKind] = useState<ActionKind>("goal");
  const [actionTeam, setActionTeam] = useState<"host" | "visitor">("host");
  const [actionPlayerIdx, setActionPlayerIdx] = useState<string>("");
  const [actionCard, setActionCard] = useState<"yellow" | "red">("yellow");
  const [actionSearch, setActionSearch] = useState("");

  const [regOpen, setRegOpen] = useState(false);
  const [regTeam, setRegTeam] = useState<"host" | "visitor">("visitor");
  const [regName, setRegName] = useState("");
  const [regJersey, setRegJersey] = useState("");
  const [regCpf, setRegCpf] = useState("");
  const [regBirth, setRegBirth] = useState("");
  const [regLoadingCpf, setRegLoadingCpf] = useState(false);
  const [showSignatures, setShowSignatures] = useState(false);
  const [pdfPromptOpen, setPdfPromptOpen] = useState(false);
  const cpfLookup = useServerFn(lookupCpfPublic);



  useEffect(() => {
    (async () => {
      try {
        const r: any = await getFn({ data: { id, token: token || null } });
        setMeta(r);
        const s = r.scoresheet;
        setRefereeName(s.referee_name ?? "");
        setRefereeReport(s.referee_report ?? "");
        setVisitorName(s.visitor_team_name || r.visitorName || "");
        const norm = (arr: any[]): Player[] => (arr ?? []).map((p) => ({
          ...p,
          goals: p.goals ?? 0,
          yellow_cards: p.yellow_cards ?? 0,
          red_cards: p.red_cards ?? 0,
          fouls: p.fouls ?? 0,
        }));
        setHost(norm(s.host_players));
        setVisitor(norm(s.visitor_players));
        setSigReferee(s.signature_referee ?? null);
        setSigHost(s.signature_host_captain ?? null);
        setSigVisitor(s.signature_visitor_captain ?? null);
        setSigEpoch((n) => n + 1);
      } catch (e: any) {
        setError(e?.message || "Não foi possível carregar a súmula.");
      } finally { setLoading(false); }
    })();
  }, [id, token, getFn]);

  const updHost = (i: number, k: keyof Player, v: any) =>
    setHost((p) => p.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const updVis = (i: number, k: keyof Player, v: any) =>
    setVisitor((p) => p.map((x, idx) => idx === i ? { ...x, [k]: v } : x));

  const openAction = (kind: ActionKind) => {
    setActionKind(kind);
    setActionTeam("host");
    setActionPlayerIdx("");
    setActionSearch("");
    setActionCard("yellow");
    setActionOpen(true);
  };

  const applyAction = () => {
    if (actionPlayerIdx === "" && actionKind !== "foul") {
      return toast.error("Selecione o atleta.");
    }
    const setFn = actionTeam === "host" ? setHost : setVisitor;
    if (actionKind === "foul" && actionPlayerIdx === "") {
      const teamName = actionTeam === "host" ? "Mandante" : "Visitante";
      const prevArr = actionTeam === "host" ? host : visitor;
      const prevTotal = prevArr.reduce((s, p) => s + (p.fouls || 0), 0);
      setFn((prev) => {
        const teamIdx = prev.findIndex((p) => !p.student_id && p.name === "— Equipe —");
        if (teamIdx >= 0) return prev.map((p, i) => i === teamIdx ? { ...p, fouls: (p.fouls || 0) + 1 } : p);
        return [...prev, { name: "— Equipe —", goals: 0, yellow_cards: 0, red_cards: 0, fouls: 1 }];
      });
      toast.success(`Falta da equipe registrada (${teamName}).`);
      {
        const msg = foulAlertMessage(teamName, prevTotal + 1, meta?.scoresheet?.modality);
        if (msg) setRefereeAlert(msg);
      }
      setActionOpen(false);
      return;
    }
    const idx = Number(actionPlayerIdx);
    const prevArr = actionTeam === "host" ? host : visitor;
    const teamName = actionTeam === "host" ? "Mandante" : "Visitante";
    const prevTeamFouls = prevArr.reduce((s, p) => s + (p.fouls || 0), 0);
    setFn((prev) => prev.map((p, i) => {
      if (i !== idx) return p;
      if (actionKind === "goal") return { ...p, goals: (p.goals || 0) + 1 };
      if (actionKind === "foul") return { ...p, fouls: (p.fouls || 0) + 1 };
      if (actionCard === "yellow") {
        const y = (p.yellow_cards || 0) + 1;
        if (y >= 2) setRefereeAlert(`${p.name}: 2º cartão amarelo. Aplique cartão VERMELHO — avise o árbitro.`);
        return { ...p, yellow_cards: y };
      }
      return { ...p, red_cards: (p.red_cards || 0) + 1 };
    }));
    if (actionKind === "foul") {
      const msg = foulAlertMessage(teamName, prevTeamFouls + 1, meta?.scoresheet?.modality);
      if (msg) setRefereeAlert(msg);
    }
    const playersRef = actionTeam === "host" ? host : visitor;
    const name = playersRef[idx]?.name ?? "";
    const kindLabel = actionKind === "goal" ? "Gol" : actionKind === "foul" ? "Falta" : (actionCard === "yellow" ? "Cartão amarelo" : "Cartão vermelho");
    toast.success(`${kindLabel}: ${name}.`);
    setActionOpen(false);
  };

  const doCpfLookup = async () => {
    const digits = regCpf.replace(/\D/g, "");
    if (digits.length !== 11) return toast.error("CPF deve ter 11 dígitos.");
    setRegLoadingCpf(true);
    try {
      const r: any = await cpfLookup({ data: { cpf: digits } });
      if (r?.ok) {
        if (r.name) setRegName(r.name);
        if (r.birth_date) setRegBirth(r.birth_date);
        toast.success("Dados preenchidos pelo CPF.");
      } else {
        toast.message(r?.error || "CPF não localizado — preencha manualmente.");
      }
    } catch (e: any) {
      toast.error(e?.message || "Falha ao consultar CPF.");
    } finally { setRegLoadingCpf(false); }
  };

  const applyRegister = () => {
    const n = regName.trim();
    if (!n) return toast.error("Informe o nome do atleta.");
    if (!regBirth) return toast.error("Informe a data de nascimento.");
    const teamArr = regTeam === "host" ? host : visitor;
    const jersey = regJersey.trim();
    if (jersey && teamArr.some((p) => (p.jersey_number ?? "").trim() === jersey)) {
      return toast.error(`Nº ${jersey} já usado nessa equipe.`);
    }
    const newP: Player = {
      name: n, jersey_number: jersey || null,
      cpf: regCpf.replace(/\D/g, "") || null,
      birth_date: regBirth || null,
      goals: 0, yellow_cards: 0, red_cards: 0, fouls: 0, present: true,
    };
    if (regTeam === "host") setHost((p) => [...p, newP]);
    else setVisitor((p) => [...p, newP]);
    toast.success(`Atleta cadastrado na equipe ${regTeam === "host" ? "mandante" : "visitante"}.`);
    setRegName(""); setRegJersey(""); setRegCpf(""); setRegBirth(""); setRegOpen(false);
  };



  const handleSave = async () => {
    setSaving(true);
    try {
      await saveFn({ data: {
        id, token, referee_name: refereeName, referee_report: refereeReport,
        visitor_team_name: visitorName,
        host_players: host as any, visitor_players: visitor as any,
        signature_referee: sigReferee,
        signature_host_captain: sigHost,
        signature_visitor_captain: sigVisitor,
      }});

      toast.success("Súmula atualizada.");
      setPdfPromptOpen(true);
    } catch (e: any) {

      toast.error(e?.message || "Erro ao salvar.");
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <Card className="max-w-md w-full"><CardContent className="pt-6 text-center">{error}</CardContent></Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-muted/30 py-6 px-3">
      <div className="max-w-5xl mx-auto space-y-4">
        <Card>
          <CardHeader className="border-b">
            <CardTitle className="text-center flex items-center justify-center gap-2">
              <FileText className="h-5 w-5" /> SÚMULA — {meta?.champName?.toUpperCase() || "Jogo"}
            </CardTitle>
            <p className="text-center text-sm text-muted-foreground">
              {meta?.tcName} · Data: {fmtDate(meta?.matchDate)} · Campo: {meta?.location ?? "-"}
              {meta?.champCategory ? ` · Categoria: ${meta.champCategory}` : ""}
              {meta?.matchTime ? ` · Horário: ${String(meta.matchTime).slice(0,5)}` : ""}
            </p>
            <p className="text-center text-xs text-muted-foreground pt-1">
              Link de edição aberto. Qualquer pessoa com este link pode editar.
            </p>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            <div className="rounded-lg border bg-gradient-to-br from-slate-900 to-slate-700 text-white p-4">
              <div className="grid grid-cols-3 items-center gap-2">
                <div className="text-center">
                  <div className="text-xs uppercase opacity-80 truncate">{meta?.tcName || "Mandante"}</div>
                  <div className="text-5xl font-black tabular-nums">{host.reduce((a, p) => a + (Number(p.goals) || 0), 0)}</div>
                </div>
                <div className="text-center text-2xl font-bold opacity-70">×</div>
                <div className="text-center">
                  <div className="text-xs uppercase opacity-80 truncate">{visitorName || "Visitante"}</div>
                  <div className="text-5xl font-black tabular-nums">{visitor.reduce((a, p) => a + (Number(p.goals) || 0), 0)}</div>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 border rounded-md p-3 bg-muted/20">
              <span className="text-sm font-semibold self-center mr-2">Registrar:</span>

              <Button size="lg" onClick={() => openAction("goal")}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base px-6">
                <Goal className="h-5 w-5 mr-2" />GOL
              </Button>
              <Button size="lg" onClick={() => openAction("card")}
                className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold text-base px-6">
                <Square className="h-5 w-5 mr-2" />CARTÕES
              </Button>
              <Button size="lg" onClick={() => openAction("foul")}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-base px-6">
                <Flag className="h-5 w-5 mr-2" />FALTA
              </Button>
              <Button size="sm" variant="outline" className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 self-center"
                onClick={() => { setRegTeam("visitor"); setRegName(""); setRegJersey(""); setRegCpf(""); setRegBirth(""); setRegOpen(true); }}>
                <UserPlus className="h-4 w-4 mr-1" />Cadastrar Atleta
              </Button>
              <span className="text-xs text-muted-foreground self-center ml-auto flex items-center gap-1">
                <ClipboardCheck className="h-3 w-3" /> Marque a presença na coluna "Pres."
              </span>
            </div>


            <div className="grid md:grid-cols-2 gap-6">
              <TeamTable title={`${meta?.tcName?.toUpperCase() || ""} (MANDANTE)`}
                players={host} onChange={updHost}
                onAdd={() => setHost([...host, { name: "", goals: 0, yellow_cards: 0, red_cards: 0, fouls: 0 }])}
                onRemove={(i) => setHost(host.filter((_, idx) => idx !== i))}
              />
              <div className="space-y-2">
                <Input placeholder="Nome do time visitante"
                  value={visitorName} onChange={(e) => setVisitorName(e.target.value)}
                  className="font-semibold" />
                <TeamTable title="(VISITANTE)"
                  players={visitor} onChange={updVis}
                  onAdd={() => setVisitor([...visitor, { name: "", goals: 0, yellow_cards: 0, red_cards: 0, fouls: 0 }])}
                  onRemove={(i) => setVisitor(visitor.filter((_, idx) => idx !== i))}
                />
              </div>
            </div>

            <div>
              <Label>Relatório do Árbitro</Label>
              <Textarea rows={4} value={refereeReport}
                onChange={(e) => setRefereeReport(e.target.value)}
                placeholder="Ocorrências, lesões, comportamento das equipes..." />
            </div>
            <div>
              <Label>Nome do Árbitro</Label>
              <Input value={refereeName} onChange={(e) => setRefereeName(e.target.value)} />
            </div>

            <div className="pt-2">
              <Button variant="outline" size="sm" onClick={() => setShowSignatures((v) => !v)}>
                <PenLine className="h-4 w-4 mr-2" />
                {showSignatures ? "Ocultar assinaturas" : "Assinar súmula"}
              </Button>
            </div>
            {showSignatures && (
              <div className="grid md:grid-cols-3 gap-4 pt-2">
                <div className="space-y-1">
                  <Label className="text-xs uppercase text-muted-foreground">Assinatura Árbitro</Label>
                  <SignaturePad key={`sr-${sigEpoch}`} value={sigReferee} onChange={setSigReferee} label="Assine com dedo ou mouse" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase text-muted-foreground">Assinatura CT Mandante</Label>
                  <SignaturePad key={`sh-${sigEpoch}`} value={sigHost} onChange={setSigHost} label="Assine com dedo ou mouse" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase text-muted-foreground">Assinatura CT Visitante</Label>
                  <SignaturePad key={`sv-${sigEpoch}`} value={sigVisitor} onChange={setSigVisitor} label="Assine com dedo ou mouse" />
                </div>
              </div>
            )}


            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              {meta?.closed && (
                <span className="text-xs font-medium text-destructive">
                  Jogo encerrado — súmula bloqueada para edição.
                </span>
              )}
              <Button
                onClick={handleSave}
                disabled={saving || !!meta?.closed}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Salvar Súmula
              </Button>
            </div>

          </CardContent>
        </Card>
      </div>

      <Dialog open={actionOpen} onOpenChange={setActionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionKind === "goal" && "Registrar Gol"}
              {actionKind === "card" && "Registrar Cartão"}
              {actionKind === "foul" && "Registrar Falta"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Equipe</Label>
              <Select value={actionTeam} onValueChange={(v) => { setActionTeam(v as any); setActionPlayerIdx(""); setActionSearch(""); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="host">{meta?.tcName || "Mandante"} (Mandante)</SelectItem>
                  <SelectItem value="visitor">{visitorName || "Visitante"} (Visitante)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Atleta</Label>
              <Input
                className="mb-2"
                placeholder="Buscar por nome ou número..."
                value={actionSearch}
                onChange={(e) => setActionSearch(e.target.value)}
              />
              <Select value={actionPlayerIdx} onValueChange={setActionPlayerIdx}>
                <SelectTrigger><SelectValue placeholder="-- Selecione o atleta --" /></SelectTrigger>
                <SelectContent>
                  {(actionTeam === "host" ? host : visitor)
                    .map((p, i) => ({ p, i }))
                    .filter(({ p }) => {
                      const q = actionSearch.trim().toLowerCase();
                      if (!q) return true;
                      return (p.name || "").toLowerCase().includes(q)
                        || String(p.jersey_number ?? "").toLowerCase().includes(q);
                    })
                    .map(({ p, i }) => (
                      <SelectItem key={i} value={String(i)}>
                        {p.jersey_number ? `${p.jersey_number} · ` : ""}{p.name || "(sem nome)"}
                      </SelectItem>
                    ))}
                  {(actionTeam === "host" ? host : visitor).length === 0 && (
                    <div className="px-3 py-2 text-sm text-muted-foreground">Nenhum atleta na equipe.</div>
                  )}
                </SelectContent>
              </Select>
            </div>
            {actionKind === "card" && (
              <div>
                <Label>Tipo de cartão</Label>
                <Select value={actionCard} onValueChange={(v) => setActionCard(v as any)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yellow">🟨 Amarelo</SelectItem>
                    <SelectItem value="red">🟥 Vermelho</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionOpen(false)}>Cancelar</Button>
            <Button onClick={applyAction} disabled={actionKind !== "foul" && actionPlayerIdx === ""}>Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!refereeAlert} onOpenChange={(o) => { if (!o) setRefereeAlert(""); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>⚠️ Avise o árbitro</DialogTitle></DialogHeader>
          <p className="text-sm py-2">{refereeAlert}</p>
          <DialogFooter>
            <Button onClick={() => setRefereeAlert("")}>OK, avisei o árbitro</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={regOpen} onOpenChange={setRegOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cadastrar Atleta</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Equipe</Label>
              <Select value={regTeam} onValueChange={(v) => setRegTeam(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="host">{meta?.tcName || "Mandante"} (Mandante)</SelectItem>
                  <SelectItem value="visitor">{visitorName || "Visitante"} (Visitante)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>CPF (opcional, preenche nome/nascimento)</Label>
              <div className="flex gap-2">
                <Input value={regCpf} onChange={(e) => setRegCpf(maskCpf(e.target.value))}
                  placeholder="000.000.000-00" autoFocus inputMode="numeric" />
                <Button type="button" onClick={doCpfLookup} disabled={regLoadingCpf}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  {regLoadingCpf ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-1" />Buscar</>}
                </Button>
              </div>
            </div>
            <div>
              <Label>Nome do atleta *</Label>
              <Input value={regName} onChange={(e) => setRegName(e.target.value)} placeholder="Nome completo" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Data de nascimento *</Label>
                <Input type="date" value={regBirth} onChange={(e) => setRegBirth(e.target.value)} />
              </div>
              <div>
                <Label>Idade</Label>
                <Input readOnly value={calcAge(regBirth) ?? ""} className="bg-muted" placeholder="—" />
              </div>
            </div>
            <div>
              <Label>Nº da camisa (opcional)</Label>
              <Input value={regJersey} onChange={(e) => setRegJersey(e.target.value)} placeholder="Ex: 10" />
              <p className="text-[11px] text-muted-foreground mt-1">Deve ser diferente dos números já usados na equipe.</p>
            </div>
            <p className="text-xs text-muted-foreground">Cadastro pelo CPF ou manual. Nome e nascimento obrigatórios.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegOpen(false)}>Cancelar</Button>
            <Button onClick={applyRegister} disabled={!regName.trim() || !regBirth}>Cadastrar</Button>
          </DialogFooter>

        </DialogContent>
      </Dialog>

      <Dialog open={pdfPromptOpen} onOpenChange={setPdfPromptOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Baixar PDF da súmula?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            A súmula foi atualizada. Deseja gerar e baixar o PDF agora?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPdfPromptOpen(false)}>Agora não</Button>
            <Button onClick={async () => {
              setPdfPromptOpen(false);
              try {
                const data: ScoresheetPdfData = {
                  tcName: meta?.tcName ?? "",
                  champName: meta?.champName ?? "",
                  champCategory: meta?.champCategory ?? "",
                  matchDate: fmtDate(meta?.matchDate),
                  matchTime: meta?.matchTime ? String(meta.matchTime).slice(0, 5) : "-",
                  location: meta?.location ?? "-",
                  visitorName,
                  host: host as any,
                  visitor: visitor as any,
                  refereeName,
                  refereeReport,
                  modality: meta?.scoresheet?.modality ?? "QUADRA",
                };
                await openScoresheetPdf(data);
              } catch (e: any) { toast.error(e?.message || "Erro ao gerar PDF"); }
            }}>Baixar PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>

  );
}

function TeamTable({
  title, players, onChange, onAdd, onRemove,
}: {
  title: string; players: Player[];
  onChange: (i: number, k: keyof Player, v: any) => void;
  onAdd: () => void; onRemove: (i: number) => void;
}) {
  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-sm">{title}</h3>
      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-muted">
            <tr>
              <th className="p-2 w-10 text-center">Pres.</th>
              <th className="p-2 text-left w-10">Nº</th>
              <th className="p-2 text-left">Atleta</th>
              <th className="p-2 w-12">Gols</th>
              <th className="p-2 w-12">CA</th>
              <th className="p-2 w-12">CV</th>
              <th className="p-2 w-12">Faltas</th>
              <th className="p-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {players.map((p, i) => (
              <tr key={i} className={`border-t ${p.is_above_category ? "bg-destructive/10" : ""} ${p.present === false ? "opacity-60" : ""}`}>
                <td className="p-1 text-center">
                  <input type="checkbox" className="h-4 w-4 accent-emerald-600"
                    checked={p.present !== false}
                    onChange={(e) => onChange(i, "present", e.target.checked)} />
                </td>
                <td className="p-1"><Input className="h-7 px-1 text-center" value={p.jersey_number ?? ""} onChange={(e) => onChange(i, "jersey_number", e.target.value)} /></td>
                <td className="p-1">
                  {p.student_id ? (
                    <span className="px-2">{p.name}{p.is_above_category && p.category && (<span className="text-[10px] font-semibold text-destructive uppercase ml-1">{p.category}</span>)}</span>
                  ) : (
                    <Input className="h-7" value={p.name} onChange={(e) => onChange(i, "name", e.target.value)} placeholder="Nome do atleta" />
                  )}
                </td>
                <td className="p-1"><Input readOnly tabIndex={-1} className="h-7 px-1 text-center bg-muted/50" value={p.goals ?? 0} /></td>
                <td className="p-1"><Input readOnly tabIndex={-1} className="h-7 px-1 text-center bg-yellow-100" value={p.yellow_cards ?? 0} /></td>
                <td className="p-1"><Input readOnly tabIndex={-1} className="h-7 px-1 text-center bg-red-100" value={p.red_cards ?? 0} /></td>
                <td className="p-1"><Input readOnly tabIndex={-1} className="h-7 px-1 text-center bg-muted/50" value={p.fouls ?? 0} /></td>
                <td className="p-1 text-center">
                  {!p.student_id && (
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => onRemove(i)}><Trash2 className="h-3 w-3" /></Button>
                  )}
                </td>
              </tr>
            ))}
            {players.length === 0 && (
              <tr><td colSpan={8} className="p-4 text-center text-muted-foreground">Nenhum atleta.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Button size="sm" variant="outline" onClick={onAdd}><Plus className="h-3 w-3 mr-1" />Adicionar Atleta</Button>
    </div>
  );
}
